import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, X, AlertCircle } from "lucide-react";
import axios from "axios";

export const UploadPdfDialog = ({ open, onOpenChange, nodes, apiUrl, onSuccess }) => {
  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setError('Sila pilih fail PDF sahaja');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('Saiz fail terlalu besar (max 50MB)');
      return;
    }
    setFile(selectedFile);
    setError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file || !selectedNodeId) {
      setError('Sila pilih organ/topik dan fail PDF');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError("");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('node_id', selectedNodeId);

    try {
      await axios.post(`${apiUrl}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setProgress(pct);
        }
      });

      setFile(null);
      setSelectedNodeId("");
      setProgress(0);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal memuat naik fail');
    } finally {
      setUploading(false);
    }
  };

  const resetState = () => {
    setFile(null);
    setSelectedNodeId("");
    setProgress(0);
    setError("");
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); onOpenChange(v); }}>
      <DialogContent
        className="sm:max-w-md bg-white"
        data-testid="upload-pdf-dialog"
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Poppins, sans-serif' }}>
            Muat Naik PDF
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Pilih organ/topik dan muat naik fail PDF
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Node Selector */}
          <div>
            <label className="text-xs font-medium mb-1.5 block text-gray-600">
              Pilih Organ / Topik
            </label>
            <select
              value={selectedNodeId}
              onChange={(e) => setSelectedNodeId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-sm border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="upload-pdf-node-select"
            >
              <option value="">-- Pilih lokasi --</option>
              {nodes.map(n => (
                <option key={n.id} value={n.id}>
                  {'\u00A0'.repeat(n.depth * 3)}{n.type === 'section' ? '> ' : n.type === 'organ' ? '- ' : '  '}{n.title}
                </option>
              ))}
            </select>
          </div>

          {/* File Drop Zone */}
          <div
            className={`upload-dropzone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-pdf-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              data-testid="upload-pdf-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={24} className="text-red-500" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-800">{file.name}</p>
                  <p className="text-xs font-mono text-gray-400">
                    {(file.size / 1024).toFixed(1)}KB
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <X size={16} className="text-gray-400" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={28} className="text-gray-300" />
                <p className="text-sm text-gray-500">Seret & lepas PDF, atau klik untuk pilih</p>
                <p className="text-xs font-mono text-gray-400">Max 50MB</p>
              </div>
            )}
          </div>

          {/* Progress */}
          {uploading && (
            <div className="space-y-2" data-testid="upload-pdf-progress">
              <Progress value={progress} className="h-2" />
              <p className="text-xs font-mono text-center text-gray-400">
                {progress}% memuat naik...
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          {/* Upload button */}
          <Button
            onClick={handleUpload}
            disabled={!file || !selectedNodeId || uploading}
            className="w-full btn-press bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-200 disabled:text-gray-400"
            data-testid="upload-pdf-submit-button"
          >
            <Upload size={16} className="mr-2" />
            {uploading ? 'Memuat naik...' : 'Muat Naik'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
